import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  hasConsentBeenSet, 
  setAnalyticsConsent, 
  hasAnalyticsConsent 
} from '@/utils/analytics';

/**
 * Cookie Consent Banner
 * GDPR-compliant cookie consent for analytics
 * Only shows if consent hasn't been set yet
 */
const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Only show banner if consent hasn't been explicitly set
    if (!hasConsentBeenSet()) {
      // Small delay to avoid layout shift on page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    setAnalyticsConsent(true);
    setShowBanner(false);
  };

  const handleDecline = () => {
    setAnalyticsConsent(false);
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-slide-up"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg border border-[#E5E7EB] p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Content */}
          <div className="flex-1">
            <h3 className="font-semibold text-[#2D4A3E] mb-1">
              We value your privacy
            </h3>
            <p className="text-sm text-[#6B7280]">
              We use non-essential cookies (such as analytics) to understand how you use our website and improve your experience. 
              We do not use cookies for advertising or remarketing. 
              You can accept or decline these cookies below.
            </p>
          </div>
          
          {/* Buttons */}
          <div className="flex items-center gap-3 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDecline}
              className="text-[#6B7280] border-[#E5E7EB]"
            >
              Decline
            </Button>
            <Button
              size="sm"
              onClick={handleAccept}
              className="bg-[#2D4A3E] hover:bg-[#1d332a] text-white"
            >
              Accept Analytics
            </Button>
          </div>
          
          {/* Close button (acts as decline) */}
          <button
            onClick={handleDecline}
            className="absolute top-2 right-2 md:hidden p-1 text-[#9CA3AF] hover:text-[#6B7280]"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Privacy link */}
        <p className="text-xs text-[#9CA3AF] mt-3 md:mt-2">
          Learn more in our{' '}
          <a href="/privacy" className="underline hover:text-[#6B7280]">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
};

/**
 * Mini consent manager for users who want to change their preference
 * Can be placed in footer or settings page
 */
export const ConsentManager = () => {
  const [consent, setConsent] = useState(hasAnalyticsConsent());
  
  const toggleConsent = () => {
    const newConsent = !consent;
    setAnalyticsConsent(newConsent);
    setConsent(newConsent);
  };
  
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-[#6B7280]">Analytics cookies:</span>
      <button
        onClick={toggleConsent}
        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
          consent 
            ? 'bg-green-100 text-green-700' 
            : 'bg-gray-100 text-gray-600'
        }`}
      >
        {consent ? 'Enabled' : 'Disabled'}
      </button>
    </div>
  );
};

export default CookieConsent;
